import React, { ComponentProps } from "react";

import AppContextValueProvider from "../../../App/compositions/AppContextValueProvider";
import ConvertExperimentContextValueProvider from "../../../Experiment/compositions/ExperimentContextValueProvider";
import { IFlowStepProps } from "../../../Flows/types/IFlow";
import FlowStepOutletValueProvider from "../../../routing/composition/FlowStepOutletValueProvider";
import ServiceSelector from "../../CenterSelect/Components/ServiceSelector";
import { IStepWrapperButtonProps } from "../../Components/StepWrapper";
import AppContextReservationCounter from "../../compositions/AppContextReservationCounter";
import SchedulingConfirmStepWrapper, {
  ISchedulingConfirmStepWrapperConfig,
  ISchedulingConfirmStepWrapperExperimentProps,
  ISchedulingConfirmStepWrapperProps
} from "../Components/SchedulingConfirmStepWrapper";
import useGetCalendarEventUrl from "../hooks/useGetCalendarEventUrl";
import AppContextSchedulingStepWithAddress from "./AppContextSchedulingStepWithAddress";
import AppContextUseConfirmAppointment from "./AppContextUseConfirmAppointment";
import AppContextUseCreateReservation from "./AppContextUseCreateReservation";
import AppContextUseReleaseReservation from "./AppContextUseReleaseReservation";
import DynamicScheduleSlotSelector from "./DynamicScheduleSlotSelector";

const RoutedSchedulingConfirmStepWrapper = FlowStepOutletValueProvider({
  Component: SchedulingConfirmStepWrapper
});

const ExperimentContextConfirmWrapper = ConvertExperimentContextValueProvider<
  ISchedulingConfirmStepWrapperExperimentProps &
    Pick<ISchedulingConfirmStepWrapperConfig, "useConfirmAppointment" | "useGetCalendarEventUrl">,
  ComponentProps<typeof RoutedSchedulingConfirmStepWrapper>
>({
  Component: RoutedSchedulingConfirmStepWrapper,
  map: ({ recordBookedAppointment }) => ({
    recordBookedAppointment,
    useConfirmAppointment: AppContextUseConfirmAppointment,
    useGetCalendarEventUrl: useGetCalendarEventUrl
  })
});

const SchedulingStepWithAddress: ISchedulingConfirmStepWrapperConfig["Scheduler"] = props => (
  <AppContextSchedulingStepWithAddress
    {...props}
    useReleaseReservation={AppContextUseReleaseReservation}
    ServiceSelector={ServiceSelector}
    SlotSelector={DynamicScheduleSlotSelector}
    ReservationCounter={AppContextReservationCounter}
  />
);

const AppContextSchedulingConfirmStepWrapper: React.FC<IStepWrapperButtonProps> = AppContextValueProvider<
  Omit<ISchedulingConfirmStepWrapperProps, keyof IFlowStepProps> &
    Pick<ISchedulingConfirmStepWrapperConfig, "useCreateReservation" | "Scheduler">,
  ComponentProps<typeof ExperimentContextConfirmWrapper>
>({
  Component: ExperimentContextConfirmWrapper,
  map: ({ bookingController, patient, contactId, reservation, router, setReservation }) => {
    if (!bookingController) throw new Error("Booking controller is required for scheduling confirm step.");
    return {
      value: reservation?.slot ?? null,
      onChange: setReservation,
      allCentersUnbookable: !bookingController.isBookingPossible,
      patient: patient,
      contactId: contactId,
      reservation: reservation ?? null,
      router,
      useCreateReservation: AppContextUseCreateReservation,
      Scheduler: SchedulingStepWithAddress
    };
  }
});

export default AppContextSchedulingConfirmStepWrapper;
